import { useCallback, useEffect, useMemo, useState } from 'react';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import { ErrorResponse } from '@core/types/errorResponse';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { ReportTemplate } from '../types/reportTemplate';
import { useReportTemplates } from './useReportTemplates';
import {
  fetchChurchTemplates,
  syncTemplatesToChurch,
  syncTemplatesToDistrict,
} from '@features/church/api/churchApi';
import { syncNationReportTemplates } from '@features/report/activity-entry/api/activityEntryApi';
import {
  fetchAssignedTemplateForDistrictAsync,
  fetchAssignedTemplateForNationAsync,
} from '../services/reportTemplateSvc';
import { useDepartments } from '@features/department/hooks/useDepartments';
import {
  SORT_BY_DEPARTMENT_NAME,
  SORT_BY_REPORT_TEMPLATE_NAME,
  SORT_DIR_ASC,
} from '@core/constants/listSort';
import {
  buildActiveLookup,
  resolveAssignedRowDisplay,
  sortAttachedByOrder,
} from '@core/components/assign/assignDisplay';
import { renumber, setItemOrder } from '@core/components/assign/assignListOrder';
import {
  AssignReportTemplatesScope,
  AttachedReportTemplate,
  ASSIGN_REPORT_TEMPLATES_SCOPE_LABEL,
} from '../types/reportTemplateAssignment';
import ApiResponse from '@core/types/apiResponse';
import { todayDateOnly } from '@core/utils/formatDateOnly';

function extractItemsFromResponse(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  const data = raw as { data?: unknown };
  if (Array.isArray(data?.data)) return data.data;
  const nested = data?.data as { data?: unknown[] } | undefined;
  if (nested && Array.isArray(nested.data)) return nested.data;
  return [];
}

function normalizeAttached(items: unknown[]): AttachedReportTemplate[] {
  return renumber(
    items
      .filter((t): t is Record<string, unknown> => Boolean((t as Record<string, unknown>)?.report_template_id))
      .map((t, index) => ({
        report_template_id: t.report_template_id as number,
        list_order: (t.list_order as number | undefined) ?? index + 1,
        is_required: (t.is_required as boolean | undefined) ?? false,
        required_at: (t.required_at as string | null | undefined) ?? null,
        report_template_name: t.report_template_name as string | undefined,
        description: (t.report_template_description ?? t.description ?? null) as string | null,
      }))
  );
}

export type UseAssignReportTemplatesOptions = {
  show: boolean;
  scope: AssignReportTemplatesScope;
  entityId: number;
  districtId?: number;
  nationId?: number;
  onHide: () => void;
  onSaved?: () => void;
};

export function useAssignReportTemplates({
  show,
  scope,
  entityId,
  districtId,
  nationId,
  onHide,
  onSaved,
}: UseAssignReportTemplatesOptions) {
  const [allTemplates, setAllTemplates] = useState<ReportTemplate[]>([]);
  const [attached, setAttached] = useState<AttachedReportTemplate[]>([]);
  const [initialAttachedIds, setInitialAttachedIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(0);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [qLeft, setQLeft] = useState('');
  const [qRight, setQRight] = useState('');

  const showError = useApiError();
  const originScope = scope;

  const attachedIds = useMemo(() => attached.map((x) => x.report_template_id), [attached]);

  const { departments, loading: loadingDepartments } = useDepartments({
    all: true,
    isActive: true,
    sortBy: SORT_BY_DEPARTMENT_NAME,
    sortDir: SORT_DIR_ASC,
    ...(nationId ? { nationId } : districtId ? { districtId } : {}),
  });

  const selectedDepartmentName = useMemo(() => {
    const dept = departments.find((d) => d.department_id === selectedDepartmentId);
    return dept?.name ?? 'this department';
  }, [departments, selectedDepartmentId]);

  const scopeLabel = ASSIGN_REPORT_TEMPLATES_SCOPE_LABEL[scope];

  const { data: templateData, loading: loadingTemplates } = useReportTemplates({
    departmentId: selectedDepartmentId,
    originScope,
    all: true,
    isActive: true,
    sortBy: SORT_BY_REPORT_TEMPLATE_NAME,
    sortDir: SORT_DIR_ASC,
  });

  useEffect(() => {
    if (templateData) {
      setAllTemplates(templateData);
    }
  }, [templateData]);

  useEffect(() => {
    if (!show) return;
    setAttached([]);
    setInitialAttachedIds([]);
    setShowClearAllConfirm(false);
    setQLeft('');
    setQRight('');
  }, [show, selectedDepartmentId]);

  useEffect(() => {
    if (departments.length > 0 && selectedDepartmentId === 0) {
      setSelectedDepartmentId(departments[0].department_id);
    }
  }, [departments, selectedDepartmentId]);

  useEffect(() => {
    if (!show || entityId <= 0 || selectedDepartmentId <= 0) return;
    if (scope === 'CHURCH' && !entityId) return;

    let cancelled = false;

    const loadAssigned = async () => {
      setLoadingAssigned(true);
      try {
        let response: ApiResponse<unknown>;
        if (scope === 'CHURCH') {
          response = await fetchChurchTemplates(entityId, selectedDepartmentId);
        } else if (scope === 'DISTRICT') {
          response = await fetchAssignedTemplateForDistrictAsync(
            entityId,
            selectedDepartmentId,
            { all: true, page: 1, perPage: 100 }
          );
        } else {
          response = await fetchAssignedTemplateForNationAsync(
            entityId,
            selectedDepartmentId,
            { all: true, page: 1, perPage: 100 }
          );
        }

        if (cancelled) return;

        if (response.isSuccess()) {
          const raw = response.getData?.();
          const normalized = normalizeAttached(extractItemsFromResponse(raw));
          setAttached(normalized);
          setInitialAttachedIds(normalized.map((x) => x.report_template_id));
        } else {
          setAttached([]);
          setInitialAttachedIds([]);
        }
      } catch {
        if (!cancelled) {
          setAttached([]);
          setInitialAttachedIds([]);
        }
      } finally {
        if (!cancelled) setLoadingAssigned(false);
      }
    };

    void loadAssigned();

    return () => {
      cancelled = true;
    };
  }, [show, scope, entityId, selectedDepartmentId]);

  const activeTemplateIds = useMemo(
    () => new Set(allTemplates.map((t) => t.report_template_id)),
    [allTemplates]
  );

  const activeTemplateLookup = useMemo(
    () =>
      buildActiveLookup(
        allTemplates,
        (t) => t.report_template_id,
        (t) => t.report_template_name,
        (t) => t.description ?? undefined
      ),
    [allTemplates]
  );

  const left = useMemo(() => {
    const base = allTemplates.filter((t) => !attachedIds.includes(t.report_template_id));
    if (!qLeft.trim()) return base;
    const s = qLeft.toLowerCase();
    return base.filter((t) => t.report_template_name.toLowerCase().includes(s));
  }, [allTemplates, attachedIds, qLeft]);

  const visibleAttached = useMemo(() => {
    const ordered = sortAttachedByOrder(attached);
    if (!qRight.trim()) return ordered;
    const s = qRight.toLowerCase();
    return ordered.filter((row) => {
      const display = resolveAssignedRowDisplay(
        row.report_template_id,
        activeTemplateIds,
        activeTemplateLookup,
        {
          title: row.report_template_name ?? `Template #${row.report_template_id}`,
          sublabel: row.description ?? undefined,
        }
      );
      return (
        display.title.toLowerCase().includes(s) ||
        (display.sublabel?.toLowerCase().includes(s) ?? false)
      );
    });
  }, [attached, qRight, activeTemplateIds, activeTemplateLookup]);

  const isLoading = loadingDepartments || loadingTemplates || loadingAssigned;

  const saveDisabled =
    isLoading || saving || (attached.length === 0 && initialAttachedIds.length === 0);

  const assignTemplate = (id: number) => {
    const template = allTemplates.find((t) => t.report_template_id === id);
    setAttached((prev) => {
      if (prev.some((x) => x.report_template_id === id)) return prev;
      return renumber([
        ...prev,
        {
          report_template_id: id,
          list_order: prev.length + 1,
          is_required: true,
          required_at: todayDateOnly(),
          report_template_name: template?.report_template_name,
          description: template?.description ?? null,
        },
      ]);
    });
  };

  const unassignTemplate = (id: number) => {
    setAttached((prev) => renumber(prev.filter((x) => x.report_template_id !== id)));
  };

  const handleOrderChange = (id: number, newOrder: number) => {
    setAttached((prev) => setItemOrder(prev, id, newOrder, (x) => x.report_template_id));
  };

  const updateAttached = (id: number, updates: Partial<Omit<AttachedReportTemplate, 'report_template_id'>>) => {
    setAttached((prev) =>
      prev.map((x) => (x.report_template_id === id ? { ...x, ...updates } : x))
    );
  };

  const handleClose = () => {
    setQLeft('');
    setQRight('');
    setShowClearAllConfirm(false);
    setSelectedDepartmentId(departments.length > 0 ? departments[0].department_id : 0);
    onHide();
  };

  const performSave = useCallback(
    async (confirmClearAll = false) => {
      if (entityId <= 0 || !selectedDepartmentId) return;
      if (scope === 'CHURCH' && !districtId) return;

      setSaving(true);

      try {
        const templates = renumber(attached).map((x) => ({
          report_template_id: x.report_template_id,
          list_order: x.list_order,
          is_required: x.is_required,
          required_at: x.is_required ? x.required_at : null,
        }));

        const syncOptions = confirmClearAll ? { confirmClearAll: true } : undefined;

        let response: ApiResponse<unknown>;
        if (scope === 'CHURCH') {
          response = await syncTemplatesToChurch(
            districtId!,
            entityId,
            selectedDepartmentId,
            templates,
            syncOptions
          );
        } else if (scope === 'DISTRICT') {
          response = await syncTemplatesToDistrict(
            entityId,
            selectedDepartmentId,
            templates,
            syncOptions
          );
        } else {
          response = await syncNationReportTemplates(
            entityId,
            selectedDepartmentId,
            templates,
            syncOptions
          );
        }

        if (!response.isSuccess()) {
          showError(response.getError() as ErrorResponse);
        } else {
          showSuccess('Report templates updated successfully');
          setShowClearAllConfirm(false);
          setQLeft('');
          setQRight('');
          onSaved?.();
          onHide();
        }
      } catch (error) {
        showError(error as ErrorResponse);
      } finally {
        setSaving(false);
      }
    },
    [
      attached,
      districtId,
      entityId,
      onHide,
      onSaved,
      scope,
      selectedDepartmentId,
      showError,
    ]
  );

  const handleSave = () => {
    if (saveDisabled) return;

    if (attached.length === 0 && initialAttachedIds.length > 0) {
      setShowClearAllConfirm(true);
      return;
    }

    void performSave(false);
  };

  const confirmClearAllSave = () => {
    void performSave(true);
  };

  const dismissClearAllConfirm = () => {
    if (!saving) {
      setShowClearAllConfirm(false);
    }
  };

  return {
    departments,
    loadingDepartments,
    selectedDepartmentId,
    selectedDepartmentName,
    scopeLabel,
    setSelectedDepartmentId,
    saving,
    isLoading,
    saveDisabled,
    showClearAllConfirm,
    confirmClearAllSave,
    dismissClearAllConfirm,
    qLeft,
    setQLeft,
    qRight,
    setQRight,
    left,
    visibleAttached,
    attached,
    assignTemplate,
    unassignTemplate,
    handleOrderChange,
    updateAttached,
    handleSave,
    handleClose,
    activeTemplateIds,
    activeTemplateLookup,
    scope,
    entityId,
    districtId,
    nationId,
  };
}

export type UseAssignReportTemplatesReturn = ReturnType<typeof useAssignReportTemplates>;
